import { useState, useEffect, useMemo } from "react"
import { useSearchParams } from "react-router-dom"
import { Search } from "lucide-react"
import { helpData, getAllCategories, searchArticles } from "@/data/helpData"
import ArticleCard from "../components/ArticleCard"
import CategoryFilter from "../components/CategoryFilter"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/Tabs"

export default function ArticlesList() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
    const initialCategory = searchParams.get('category')
    return initialCategory ? [initialCategory] : []
  })
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '')

  const articles = helpData.articles
  const categories = getAllCategories().map(cat => ({
    id: cat.toLowerCase(),
    name: cat,
    count: articles.filter(article => article.category.toLowerCase() === cat.toLowerCase()).length
  }))

  useEffect(() => {
    const initialCategory = searchParams.get('category')
    setSelectedCategories(initialCategory ? [initialCategory] : [])
    setSearchTerm(searchParams.get('search') || '')
  }, [searchParams])

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories(prev => {
      const newSelection = prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
      
      // Update URL params
      const newParams = new URLSearchParams(searchParams)
      if (newSelection.length > 0) {
        newParams.set('category', newSelection[0]) // Just use first category for URL
      } else {
        newParams.delete('category')
      }
      if (searchTerm) {
        newParams.set('search', searchTerm)
      }
      setSearchParams(newParams)
      
      return newSelection
    })
  }

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = event.target.value
    setSearchTerm(newSearchTerm)
    
    // Update URL params
    const newParams = new URLSearchParams(searchParams)
    if (newSearchTerm) {
      newParams.set('search', newSearchTerm)
    } else {
      newParams.delete('search')
    }
    if (selectedCategories.length > 0) {
      newParams.set('category', selectedCategories[0])
    }
    setSearchParams(newParams)
  }

  const filteredArticles = useMemo(() => {
    return articles.filter(article => {
      const categoryMatch = selectedCategories.length === 0 || 
        selectedCategories.some(selCat => article.category.toLowerCase() === selCat)
      const searchMatch = !searchTerm ||
        article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.content.toLowerCase().includes(searchTerm.toLowerCase())
      return categoryMatch && searchMatch
    })
  }, [selectedCategories, searchTerm, articles])

  const articlesByTab = useMemo(() => {
    const searchedArticles = articles.filter(article =>
      !searchTerm ||
      article.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
      article.content.toLowerCase().includes(searchTerm.toLowerCase())
    )
    
    return {
      all: searchedArticles,
      guides: searchedArticles.filter(a => a.category.toLowerCase() === 'guides'),
      tutorials: searchedArticles.filter(a => a.category.toLowerCase() === 'tutorials'),
      api: searchedArticles.filter(a => a.category.toLowerCase() === 'api'),
      billing: searchedArticles.filter(a => a.category.toLowerCase() === 'billing')
    }
  }, [searchTerm, articles])

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-12 md:py-16">
        {/* Header */}
        <div className="max-w-3xl mx-auto mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-bold text-purple-900 dark:text-purple-300 mb-4">
            Knowledge Base
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
            Browse our comprehensive collection of articles, guides, and tutorials.
          </p>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Search articles..."
              value={searchTerm}
              onChange={handleSearch}
              className="pl-12 pr-4 py-3.5 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-700 focus:border-purple-400 dark:focus:border-purple-600 focus:ring-4 focus:ring-purple-500/30 rounded-xl w-full dark:text-white text-base shadow-md transition-all"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Filter */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white dark:bg-gray-900 p-6 rounded-xl shadow-lg border border-gray-200 dark:border-gray-800">
              <h2 className="text-xl font-semibold text-purple-900 dark:text-purple-300 mb-5">
                Filter by Category
              </h2>
              <CategoryFilter
                categories={categories}
                selectedCategories={selectedCategories}
                onCategoryToggle={toggleCategory}
              />
            </div>
          </div>

          {/* Articles Content */}
          <div className="lg:col-span-3">
            <Tabs defaultValue="all">
              <TabsList className="flex w-full space-x-1 bg-purple-100 dark:bg-purple-900/30 p-1 rounded-xl mb-8">
                <TabsTrigger value="all">All ({articlesByTab.all.length})</TabsTrigger>
                <TabsTrigger value="guides">Guides ({articlesByTab.guides.length})</TabsTrigger>
                <TabsTrigger value="tutorials">Tutorials ({articlesByTab.tutorials.length})</TabsTrigger>
                <TabsTrigger value="api">API ({articlesByTab.api.length})</TabsTrigger>
                <TabsTrigger value="billing">Billing ({articlesByTab.billing.length})</TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="mt-6">
                {filteredArticles.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredArticles.map(article => (
                      <ArticleCard key={article.id} article={article} />
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No articles found matching your criteria.
                  </p>
                )}
              </TabsContent>

              <TabsContent value="guides" className="mt-6">
                {filteredArticles.filter(a => a.category.toLowerCase() === 'guides').length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredArticles
                      .filter(a => a.category.toLowerCase() === 'guides')
                      .map(article => (
                        <ArticleCard key={article.id} article={article} />
                      ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No Guides found matching your criteria.
                  </p>
                )}
              </TabsContent>

              <TabsContent value="tutorials" className="mt-6">
                {filteredArticles.filter(a => a.category.toLowerCase() === 'tutorials').length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredArticles
                      .filter(a => a.category.toLowerCase() === 'tutorials')
                      .map(article => (
                        <ArticleCard key={article.id} article={article} />
                      ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No Tutorials found matching your criteria.
                  </p>
                )}
              </TabsContent>

              <TabsContent value="api" className="mt-6">
                {filteredArticles.filter(a => a.category.toLowerCase() === 'api').length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredArticles
                      .filter(a => a.category.toLowerCase() === 'api')
                      .map(article => (
                        <ArticleCard key={article.id} article={article} />
                      ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No API Documentation found matching your criteria.
                  </p>
                )}
              </TabsContent>

              <TabsContent value="billing" className="mt-6">
                {filteredArticles.filter(a => a.category.toLowerCase() === 'billing').length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredArticles
                      .filter(a => a.category.toLowerCase() === 'billing')
                      .map(article => (
                        <ArticleCard key={article.id} article={article} />
                      ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">
                    No Billing articles found matching your criteria.
                  </p>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>
    </div>
  )
}